import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-geosearch/dist/geosearch.css';

interface MapCoordinatesEditorProps {
  latitude: number | null;
  longitude: number | null;
  onCoordinatesChange: (lat: number, lng: number) => void;
}

// Fix for default marker icon in Leaflet
const fixLeafletIcon = () => {
  // Fix missing icon issue
  delete (L.Icon.Default.prototype as any)._getIconUrl;
  
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  });
};

// Component to handle map events - moved to top level
const MapEventHandler = ({ onCoordinatesChange }: { onCoordinatesChange: (lat: number, lng: number) => void }) => {
  useMapEvents({
    click: (e) => {
      onCoordinatesChange(e.latlng.lat, e.latlng.lng);
    },
  });
  
  return null;
};

const MapCoordinatesEditor: React.FC<MapCoordinatesEditorProps> = ({
  latitude,
  longitude,
  onCoordinatesChange
}) => {
  // Fix: Handle undefined values properly
  const defaultLat = 43.756111; // Scandicci, Italy
  const defaultLng = 11.178889;
  
  const [position, setPosition] = useState<[number, number]>([
    (latitude !== null && latitude !== undefined) ? latitude : defaultLat,
    (longitude !== null && longitude !== undefined) ? longitude : defaultLng
  ]);
  
  const mapRef = useRef<L.Map | null>(null);
  const searchControlRef = useRef<any>(null);

  // Fix Leaflet icon issue
  useEffect(() => {
    fixLeafletIcon();
  }, []);

  // Update marker position when coordinates change
  useEffect(() => {
    if (latitude !== null && latitude !== undefined && longitude !== null && longitude !== undefined) {
      setPosition([latitude, longitude]);
      
      // Center map on new position if map is available
      if (mapRef.current) {
        mapRef.current.setView([latitude, longitude], mapRef.current.getZoom());
      }
    }
  }, [latitude, longitude]);

  // Handle marker drag
  const handleMarkerDrag = (e: L.LeafletEvent) => {
    const marker = e.target;
    const position = marker.getLatLng();
    onCoordinatesChange(position.lat, position.lng);
  };

  // Handle search results - moved useCallback to component level
  const handleSearchResults = useCallback((event: any) => {
    if (event && event.location) {
      const { x, y } = event.location;
      onCoordinatesChange(y, x); // Note: geosearch returns x=longitude, y=latitude
    }
  }, [onCoordinatesChange]);

  // Initialize search control
  useEffect(() => {
    if (mapRef.current && !searchControlRef.current) {
      const provider = new OpenStreetMapProvider({
        params: {
          'accept-language': 'it', // Italian results
          countrycodes: 'it', // Italy only
        },
      });

      // Cast GeoSearchControl to a constructable type to satisfy TS
      const searchControl = new (GeoSearchControl as unknown as { new (options: any): L.Control })({
        provider: provider,
        style: 'bar',
        showMarker: false,
        showPopup: false,
        autoClose: true,
        retainZoomLevel: false,
        animateZoom: true,
        keepResult: true,
        searchLabel: 'Cerca indirizzo...'
      });

      mapRef.current.addControl(searchControl);
      searchControlRef.current = searchControl;

      // Add event listener for search results
      mapRef.current.on('geosearch/showlocation', handleSearchResults);

      return () => {
        if (mapRef.current) {
          mapRef.current.off('geosearch/showlocation', handleSearchResults);
        }
      };
    }
  }, [handleSearchResults]);

  return (
    <div className="w-full h-[400px] rounded-lg border border-gray-300 overflow-hidden">
      <MapContainer
        center={position}
        zoom={15}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker 
          position={position} 
          draggable={true}
          eventHandlers={{
            dragend: handleMarkerDrag
          }}
        />
        <MapEventHandler onCoordinatesChange={onCoordinatesChange} />
      </MapContainer>
    </div>
  );
};

export default MapCoordinatesEditor;